import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { destinations, learningTopicDetails, learningTopics, tools, wireArticles } from "@/data/publicFoundation";
import { localizeLearningTopicDetail } from "@/components/public/learningTopicTranslations";
import { getTranslationsForLanguage } from "@/i18n";
import { adaptLearningArticles } from "@/lib/publicLearningArticleAdapter";
import LearningTopicDetail from "@/components/public/LearningTopicDetail";
import LearningTopicsSection from "@/components/public/LearningTopicsSection";
import LearningAcademyHero from "@/components/public/LearningAcademyHero";
import LearningCenterValueAdditions from "@/components/public/LearningCenterValueAdditions";
import PublicWeatherDashboard from "@/components/public/PublicWeatherDashboard";
import WeatherSeoGrowthHub from "@/components/public/WeatherSeoGrowthHub";
import MarinaDiscovery from "@/components/marinas/discovery/MarinaDiscovery";
import PublicMarinasFAQ from "@/components/public/PublicMarinasFAQ";
import MarinasApprovalGuide from "@/components/public/MarinasApprovalGuide";
import PublicBreadcrumbs from "@/components/public/PublicBreadcrumbs";
import PublicSeoTopicClusters from "@/components/public/PublicSeoTopicClusters";
import WeatherFAQSection from "@/components/weather/WeatherFAQSection";
import WeatherForecastLandingContent from "@/components/public/WeatherForecastLandingContent";
import InFeedAd from "@/components/ads/InFeedAd";
import CalculatorHub from "@/components/public/calculators/CalculatorHub";
import AdSenseApprovalEditorialBlock from "@/components/public/AdSenseApprovalEditorialBlock";
import { getLocalizedPath } from "@/lib/i18nRoutes";

export default function PublicIndexPage({ pageKey = "learn" }) {
  const { lang = "en", country, city, topic } = useParams();
  const publicCopy = getTranslationsForLanguage(lang, "public");
  const homeCopy = getTranslationsForLanguage(lang, "home");
  const selectedTopic = pageKey === "learn" && topic && learningTopicDetails[topic] ? localizeLearningTopicDetail(topic, learningTopicDetails[topic], lang) : null;
  const weatherForecastContent = publicCopy.pages.weatherForecast || publicCopy.pages.weather;
  const content = pageKey === "weatherForecast" ? weatherForecastContent : (selectedTopic || publicCopy.pages[pageKey] || publicCopy.pages.learn);
  const prefix = `/${lang}`;
  const learningCenterHref = getLocalizedPath("learningCenter", lang);
  const wireHref = getLocalizedPath("wire", lang);
  const toolsHref = getLocalizedPath("tools", lang);
  const title = country ? `${content.h1} — ${country}` : content.h1;

  const { data: topicArticles = [], isLoading: isLoadingTopicArticles } = useQuery({
    queryKey: ["public-learning-topic-articles", topic],
    queryFn: () => base44.entities.LearningContent.filter({ main_category: topic, is_published: true }, "title", 250),
    enabled: !!selectedTopic && !!topic,
    select: adaptLearningArticles,
  });

  const { data: articleCounts = {} } = useQuery({
    queryKey: ["public-learning-topic-counts"],
    queryFn: () => base44.entities.LearningContent.filter({ is_published: true }, "title", 500),
    enabled: pageKey === "learn" && !selectedTopic,
    select: (items) => items.reduce((counts, item) => {
      counts[item.main_category] = (counts[item.main_category] || 0) + 1;
      return counts;
    }, {}),
  });

  if (pageKey === "marinas") {
    const showApprovalGuide = lang === "en" && !country && !city;
    return (
      <div className="min-h-screen bg-[linear-gradient(135deg,hsl(var(--background))_0%,hsl(var(--seafoam)/.58)_46%,hsl(var(--background))_100%)] pb-10">
        <div className="maritime-container py-6 md:py-10">
          <PublicBreadcrumbs items={[{ label: publicCopy.common.home, href: prefix }, { label: title }]} />
          {showApprovalGuide ? <MarinasApprovalGuide /> : <MarinaDiscovery lang={lang} country={country} city={city} />}
          <AdSenseApprovalEditorialBlock pageKey="marinas" lang={lang} />
          <PublicSeoTopicClusters lang={lang} />
          <div className="mt-10"><PublicMarinasFAQ lang={lang} /></div>
        </div>
      </div>
    );
  }

  if (pageKey === "learn" && !selectedTopic) {
    return (
      <div
        className="min-h-screen bg-[#EAFBFC] bg-cover bg-top bg-no-repeat pb-8"
        style={{ backgroundImage: "linear-gradient(90deg, rgba(234,251,252,0.92), rgba(234,251,252,0.72)), url('https://media.base44.com/images/public/69e8e257032c3bb17c39059f/f3375a056_generated_image.png')" }}
      >
        <div className="maritime-container py-8 md:py-10">
          <PublicBreadcrumbs items={[{ label: publicCopy.common.home, href: prefix }, { label: title }]} />
          <LearningAcademyHero content={content} publicCopy={publicCopy} prefix={prefix} />
          <LearningCenterValueAdditions prefix={prefix} lang={lang} />
          <AdSenseApprovalEditorialBlock pageKey="learn" lang={lang} />
          <PublicSeoTopicClusters lang={lang} />
          <LearningTopicsSection topics={learningTopics} topicDetails={learningTopicDetails} prefix={prefix} articleCounts={articleCounts} lang={lang} />
        </div>
      </div>
    );
  }

  if (pageKey === "learn" && selectedTopic) {
    return (
      <div
        className="min-h-screen bg-[#EAFBFC] bg-cover bg-top bg-no-repeat pb-8"
        style={{ backgroundImage: "linear-gradient(90deg, rgba(234,251,252,0.94), rgba(234,251,252,0.76)), url('https://media.base44.com/images/public/69e8e257032c3bb17c39059f/f3375a056_generated_image.png')" }}
      >
        <div className="maritime-container py-8 md:py-10">
          <PublicBreadcrumbs items={[{ label: publicCopy.common.home, href: prefix }, { label: publicCopy.pages.learn.h1, href: learningCenterHref }, { label: title }]} />
          <section className="relative overflow-hidden rounded-[2rem] py-4 md:px-8 md:py-8">
            <p className="text-xs font-black uppercase tracking-[0.34em] text-[#168F92] md:text-sm">{publicCopy.common.publicGuide}</p>
            <h1 className="mt-4 max-w-full break-words [text-wrap:balance] font-heading text-[clamp(1.65rem,6.2vw,2.45rem)] font-black leading-[1.05] tracking-[-0.035em] text-[#081633] md:text-[clamp(2.25rem,4.2vw,3.25rem)]">{title}</h1>
            <p className="mt-5 max-w-3xl text-base font-medium leading-7 text-[#10233E] md:text-lg">{content.intro}</p>
          </section>
          <LearningTopicDetail topic={selectedTopic} articles={topicArticles} isLoading={isLoadingTopicArticles} prefix={prefix} topicSlug={topic} lang={lang} />
        </div>
      </div>
    );
  }

  return (
    <div className="maritime-container py-5 md:py-8">
      <PublicBreadcrumbs items={[{ label: publicCopy.common.home, href: prefix }, { label: title }]} />
      <section className="rounded-[1.5rem] border border-border bg-card p-4 shadow-lg md:p-6">
        <p className="text-xs font-black uppercase tracking-[0.2em] text-primary">{publicCopy.common.publicGuide}</p>
        <h1 className="mt-3 max-w-full break-words [text-wrap:balance] font-heading text-[clamp(1.45rem,5.4vw,2.15rem)] font-black leading-[1.04] tracking-[-0.035em] md:text-[clamp(2rem,3.6vw,2.8rem)]">{title}</h1>
        <p className="mt-3 max-w-3xl text-base leading-7 text-muted-foreground md:text-lg">{content.intro}</p>
        <div className="mt-5 flex flex-wrap gap-3"><Button asChild><Link to={wireHref}>{publicCopy.common.readLatest}</Link></Button><Button asChild variant="outline"><Link to={pageKey === "tools" ? "#calculator-tools" : toolsHref}>{publicCopy.common.tryTools}</Link></Button></div>
      </section>

      {(pageKey === "weatherForecast" || pageKey === "tools") && (
        <AdSenseApprovalEditorialBlock pageKey={pageKey === "tools" ? "tools" : pageKey} lang={lang} />
      )}

      {selectedTopic ? (
        <LearningTopicDetail topic={selectedTopic} articles={topicArticles} isLoading={isLoadingTopicArticles} prefix={prefix} topicSlug={topic} lang={lang} />
      ) : (
        <section className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          {content.bullets.map((item) => <div key={item} className="rounded-2xl border border-border bg-card p-4 text-card-foreground"><CheckCircle2 className="h-4 w-4 text-primary" /><p className="mt-2 text-sm font-bold leading-6 text-card-foreground md:text-base">{item}</p></div>) }
        </section>
      )}

      {(pageKey === "weather" || pageKey === "weatherForecast") && (
        <>
          <PublicWeatherDashboard lang={lang} />
          <PublicSeoTopicClusters lang={lang} />
          {lang === "en" && (pageKey === "weatherForecast" ? <WeatherForecastLandingContent lang={lang} /> : <WeatherSeoGrowthHub lang={lang} />)}
        </>
      )}
      {pageKey === "marinas" && <MarinaDiscovery lang={lang} country={country} city={city} />}
      {pageKey === "tools" && <CalculatorHub lang={lang} homeCopy={homeCopy} publicCopy={publicCopy} />}
      {pageKey === "destinations" && <section className="mt-6 grid gap-3 md:grid-cols-3">{destinations.map((item, index) => <React.Fragment key={item}><article className="rounded-2xl border border-border bg-card p-4"><h2 className="text-lg font-black">{item} {publicCopy.common.sailingGuide}</h2><p className="mt-1 text-sm leading-6 text-muted-foreground">{publicCopy.common.destinationText}</p></article>{index === 5 && <InFeedAd className="col-span-full" />}</React.Fragment>)}</section>}
      {pageKey === "learn" && !selectedTopic && (
        <>
          <LearningTopicsSection topics={learningTopics} topicDetails={learningTopicDetails} prefix={prefix} articleCounts={articleCounts} lang={lang} />
        </>
      )}
      {pageKey !== "tools" && pageKey !== "destinations" && pageKey !== "learn" && pageKey !== "marinas" && <section className="mt-6 grid gap-3 md:grid-cols-3">{(publicCopy.wireArticles || wireArticles).map((article, index) => <React.Fragment key={article.slug}><Link to={`${wireHref}/${article.slug}`} className="rounded-2xl border border-border bg-card p-4 hover:border-primary/50"><p className="text-[11px] font-black uppercase tracking-widest text-primary">{article.category}</p><h2 className="mt-2 text-lg font-black leading-6">{article.title}</h2><p className="mt-1 text-sm leading-6 text-muted-foreground">{article.excerpt}</p></Link>{index === 5 && <InFeedAd className="col-span-full" />}</React.Fragment>)}</section>}

      {(pageKey === "weather" || pageKey === "weatherForecast") && <div className="mt-10"><WeatherFAQSection lang={lang} /></div>}
      {pageKey === "marinas" && <PublicMarinasFAQ lang={lang} />}
    </div>
  );
}